/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { BadgesService, OrderItemsChecklistState } from '../badges.service';
import { BadgesDaoService } from '../../dao/badges-dao.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { ServicejobsService } from 'src/app/_services/servicejobs/logic/servicejobs.service';
import { IMSComponent } from 'src/app/_util/ims-component';
import { TranslateService } from '@ngx-translate/core';
import { TourStopsService } from 'src/app/_services/tour-stop/logic/tour-stop.service';
import { TourStop } from 'src/app/_model/tour-stop';
import { IMSObjectType } from 'src/app/_model/ims-object';
import { Servicejob } from 'src/app/_model/servicejob';

/**
 * Loads Badges
 */
@Injectable()
export class BadgesLogicService implements BadgesService {
  public badges$ = new BehaviorSubject<any>({});
  public initializeStatus$: BehaviorSubject<InitializeStatus> = new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private badgesDaoService: BadgesDaoService
  ) {
    this.initialize();
  }

  // Public Functions
  public async setCachedRemoteActionsBadge(
    remoteActions: number
  ): Promise<void> {
    let badges = this.badges$.getValue();
    if (badges[BadgesService.CACHED_REMOTEACTIONS] === remoteActions) {
      return;
    }
    badges[BadgesService.CACHED_REMOTEACTIONS] = remoteActions;

    this.badges$.next(badges);
    await this.badgesDaoService.saveBadges(badges);
  }

  public async setMultiInstanceAmountBadge(amounts: {
    [key: string]: number;
  }): Promise<void> {
    let badges = this.badges$.getValue();
    badges[BadgesService.MULTIINSTANCE_AMOUNT] = amounts;

    this.badges$.next(badges);
    await this.badgesDaoService.saveBadges(badges);
  }

  public async setJobSectionInstanceAmountBadge(amounts: {
    [key: string]: number;
  }): Promise<void> {
    let badges = this.badges$.getValue();
    badges[BadgesService.JOBSECTIONINSTANCE_AMOUNT] = amounts;

    this.badges$.next(badges);
    await this.badgesDaoService.saveBadges(badges);
  }

  public async setCloudDraftAmountBadges(
    myDraftAmounts: { [key: string]: number },
    serviceJobDraftAmounts: { [key: string]: number },
  ): Promise<void> {
    let badges = this.badges$.getValue();
    badges[BadgesService.CLOUDDRAFT_AMOUNT] = myDraftAmounts;
    badges[BadgesService.SERVICEJOBDRAFT_AMOUNT] = serviceJobDraftAmounts;

    this.badges$.next(badges);
    await this.badgesDaoService.saveBadges(badges);
  }

  public async setOrderItemsChecklistStateBadge(states: {
    [key: string]: OrderItemsChecklistState;
  }): Promise<void> {
    let badges = this.badges$.getValue();
    badges[BadgesService.ORDERITEMCHECKLIST_STATE] = states;

    this.badges$.next(badges);
    await this.badgesDaoService.saveBadges(badges);
  }

  public async setUnreadMessages(count: number): Promise<void> {
    let badges = this.badges$.getValue();
    badges[BadgesService.UNREAD_MESSAGES] = count

    this.badges$.next(badges);
    await this.badgesDaoService.saveBadges(badges);
  }

  // Resetable
  public async reset() {
    this.badgesDaoService.reset();
    this.badges$.next({});
  }

  public async clear() {
    await this.badgesDaoService.clear();
    this.badges$.next({});
  }


  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING)
    try {
      await this.loadLocalBadges();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED)
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE)
    }
  }

  public async clearBadgesFromNotAvailableTourStops(stops: TourStop[]) {
    if (this.initializeStatus$.getValue() !== InitializeStatus.INITIALIZED) {
      return
    }

    let stopDataKeys = stops.map(stop => stop.getDynFormDataKey())
    await this.cleanUpOrderItemChecklist(stopDataKeys)
    await this.cleanUpMultiInstance(stopDataKeys, "ims/TourStop")
  }

  public async clearBadgesFromNotAvailableServiceJobs(jobs: Servicejob[]) {
    if (this.initializeStatus$.getValue() !== InitializeStatus.INITIALIZED) {
      return
    }

    let jobDataKeys = jobs.map(job => job.getDynFormDataKey())
    await this.cleanUpMultiInstance(jobDataKeys, "ims/ServiceJob")
  }

  // Private Functions
  private async cleanUpOrderItemChecklist(availableKey: string[]) {
    let checklistBadges = this.badges$.getValue()[BadgesService.ORDERITEMCHECKLIST_STATE] ?? {}

    Object.keys(checklistBadges).forEach((key) => {
      if (!availableKey.includes(key)) {
        delete checklistBadges[key];
      }
    });
    await this.setOrderItemsChecklistStateBadge(checklistBadges)
  }

  private async cleanUpMultiInstance(availableKey: string[], objRef: IMSObjectType) {
    let multiInstanceBadges = this.badges$.getValue()[BadgesService.MULTIINSTANCE_AMOUNT] ?? {}

    Object.keys(multiInstanceBadges).forEach((key) => {
      let keyParts = key.split(";");

      if (keyParts.length === 3 && keyParts[1] == objRef && !availableKey.includes(keyParts[2])) {
        delete multiInstanceBadges[key];
      }
    });
    await this.setMultiInstanceAmountBadge(multiInstanceBadges)
  }


  private async loadLocalBadges() {
    try {
      this.logger.log(this.constructor.name, '- Load local Badges');
      let badges: any = await this.badgesDaoService.getBadges();

      if (!badges) {
        badges = {};
      }

      this.logger.log(this.constructor.name, '- Load local Badges', badges);
      this.badges$.next(badges);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Badges: ',
        error?.message
      );
      throw error;
    }
  }

  async initializeCompleted(): Promise<void> {

  }

}
